import { isAllCoachFieldFilled } from "logic/logics";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { taskApi } from "api";
import { ProgramContentType } from "data/BlockComponent";
import {
  THOUGHTRECORD_DOMAIN,
  THOUGHTRECORD_LIST_ENDPOINT,
  thoughtRecordApi,
} from "api/thoughtRecordApi";
import { useSetRecoilState } from "recoil";
import { taskListAtom } from "recoil/taskListAtom";
import {
  MEDITATION_RECORD_DOMAIN,
  MEDITATION_RECORD_LIST_ENDPOINT,
  meditationRecordApi,
} from "api/meditationRecordApi";
import useRefetchTaskList from "hooks/useRefetchTaskList";
import React, { useEffect, useState } from "react";
import useGetBlankTaskData from "hooks/useGetBlankTaskData";
import { ProgramType } from "../../../data/programData";
import { Button } from "@mui/joy";
import CustomModal from "../../common/CustomModal";
import { UndoneIconSvg } from "../../../static/image/common/SvgAssets";
import { theme } from "styles/theme";

export default function TaskUndoneButton({
  taskKey,
  setData,
  contentData,
  setContentData,
  thoughtRecordKey,
  activityRecordKey,
  meditationRecordKey,
  isDone = false,
  onClose,
}: {
  taskKey: string;
  setData?: React.Dispatch<React.SetStateAction<ProgramType | undefined>>;
  contentData?: ProgramContentType[];
  setContentData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  thoughtRecordKey?: string;
  activityRecordKey?: string;
  meditationRecordKey?: string;
  isDone?: boolean;
  onClose: () => void;
}) {
  const isThoughtRecord = taskKey.includes("2-2-T") && thoughtRecordKey;
  const isMeditationRecord = meditationRecordKey !== undefined;
  const queryClient = useQueryClient();

  const blankTaskData = useGetBlankTaskData({ taskKey: taskKey });

  const setTaskListState = useSetRecoilState(taskListAtom);

  const refetchTaskList = useRefetchTaskList();

  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState<boolean>(true);
  const [openConfirmResetModal, setOpenConfirmResetModal] = useState(false);

  useEffect(() => {
    setDisabled(!(contentData && isDone && isAllCoachFieldFilled(contentData)));
  }, [contentData, isDone]);

  const { mutate: activityUndone } = useMutation(
    () => taskApi.undone(`${taskKey}`),
    {
      onSuccess: () => {
        refetchTaskList();
        setTaskListState((prev) =>
          prev.map((task) =>
            task.taskKey === taskKey
              ? { ...task, isDone: false, isOpen: true }
              : task
          )
        );
      },
    }
  );

  const { mutate: thoughtRecordUndone } = useMutation(
    () => thoughtRecordApi.undone(`${thoughtRecordKey}`),
    {
      onSuccess: () => {
        refetchTaskList();
        queryClient.invalidateQueries([
          THOUGHTRECORD_DOMAIN,
          THOUGHTRECORD_LIST_ENDPOINT,
        ]);
        setTaskListState((prev) =>
          prev.map((task) =>
            task.taskKey === taskKey
              ? { ...task, isDone: false, isOpen: true }
              : task
          )
        );
      },
    }
  );

  const { mutate: meditationRecordUndone } = useMutation(
    () => meditationRecordApi.undone(`${meditationRecordKey}`),
    {
      onSuccess: () => {
        refetchTaskList();
        queryClient.invalidateQueries([
          MEDITATION_RECORD_DOMAIN,
          MEDITATION_RECORD_LIST_ENDPOINT,
        ]);
        setTaskListState((prev) =>
          prev.map((task) =>
            task.taskKey === taskKey
              ? { ...task, isDone: false, isOpen: true }
              : task
          )
        );
      },
    }
  );

  function handleUndone() {
    if (isThoughtRecord) {
      thoughtRecordUndone();
    } else if (isMeditationRecord) {
      meditationRecordUndone();
    } else {
      activityUndone();
    }
  }

  return (
    <>
      <Button
        size="sm"
        variant="Plain"
        disabled={disabled}
        startDecorator={<UndoneIconSvg />}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        sx={{
          "--Button-gap": "12px",
          width: "100%",
          height: "45px",
          fontSize: "16px",
          fontWeight: 500,
          pl: "20px",
          py: "12px",
          justifyContent: "flex-start",
          color: theme.vars.palette.Content.Strong,
          backgroundColor: theme.vars.palette.Surface.Forward,
        }}
      >
        {"그대로 다시 주기"}
      </Button>
      <CustomModal
        open={open}
        onClose={() => {
          setOpen(false);
          onClose();
        }}
        title={"활동지를 다시 열어줄까요?"}
        subtitle={"이용자가 내용을 수정할 수 있게 돼요."}
        actions={[
          {
            label: "다시 주기",
            onClick: () => {
              handleUndone();
              setOpen(false);
              onClose();
            },
            variant: "Solid",
            color: "primary",
          },
        ]}
      />
    </>
  );
}